import AuctionsClient from '@/api/AuctionsClient';
import MyAcvClient from '@/api/MyAcvClient';
import Auction from '@/models/Auction';
import cloneDeep from 'lodash.clonedeep';

const endpoints = {
  activeBuying: 'getMyActiveAuctions',
  activeSelling: 'getMyAcvActiveSellingAuctions',
};

function adjustAuctionStatusPills(
  highBid,
  highBidderUserId,
  highBidderDealerId,
  currentUser,
  { floorPrice, participant, autosell },
  { statusPills },
) {
  const arr = [];

  if (autosell && statusPills?.includes('no_reserve')) {
    // show no reserve if auction is no reserve always
    arr.push('no_reserve');
  } else if (autosell && highBid >= floorPrice) {
    // else show reserve met when reached
    arr.push('reserve_met');
  } else if (statusPills?.includes('near_reserve')) {
    // else show near reserve
    arr.push('near_reserve');
  }

  if (currentUser.isMemberOfWinningDealership(highBidderDealerId)
    || highBidderUserId === currentUser.id) {
    arr.push('high_bidder');
  }

  if (highBidderUserId !== currentUser.id && participant) {
    arr.push('out_bid');
  }

  // show those statuses
  return arr;
}

// only active buying and active selling subscribe to Pubnub
const subscribableRoutes = (endpoint) => ['activeBuying', 'activeSelling'].includes(endpoint);

const setupChannelKeys = (auctions) => {
  const activeAuctionChannelKeys = auctions.map((auction) => `updates.${auction.id}`);
  const endedAuctionChannelKeys = auctions
    .map((auction) => `ended_updates.${auction.id}`);

  return [
    ...activeAuctionChannelKeys,
    ...endedAuctionChannelKeys,
  ];
};

const fetchAuctionsByEndpoint = async ({ currentUser, params, endpoint }) => {
  let response;
  const endpointToUse = endpoints?.[endpoint];
  if (endpointToUse) {
    try {
      if (endpoint === 'activeBuying') {
        response = await new AuctionsClient(currentUser)[endpointToUse](params);
        return response?.results;
      }

      response = await new MyAcvClient(currentUser)[endpointToUse](params);
      return response?.data?.data?.auctions;
    } catch (err) {
      return Promise.reject(err);
    }
  } else {
    return [];
  }
};

export const actions = {
  async fetchAuctions(
    {
      commit, dispatch, rootGetters, rootState,
    },
    data,
  ) {
    const { params, endpoint } = data;
    const auctions = await fetchAuctionsByEndpoint({
      currentUser: rootGetters.currentUser,
      params,
      endpoint,
    });

    if (auctions.length) {
      if (subscribableRoutes(rootState.route.meta.endpointContext)) {
        await dispatch('subscribeAuctions', { auctions });
      }

      commit('mutateAuctions', {
        auctions,
        isActiveSelling: rootState.route.meta.endpointContext === 'activeSelling',
      });
    }
  },

  async updateAuctions(
    { commit, rootGetters, rootState },
    {
      auctionIds = [],
      retries = 3,
    },
  ) {
    const response = await new AuctionsClient(rootGetters.currentUser)
      .getAuctionsById(auctionIds, retries);

    commit('mutateAuctions', {
      auctions: response?.results,
      isActiveSelling: rootState.route.meta.endpointContext === 'activeSelling',
    });
  },

  async removeAuctions(
    {
      state, commit, dispatch, rootState,
    },
  ) {
    if (subscribableRoutes(rootState.route.meta.endpointContext)) {
      await dispatch('unsubscribeAuctions', { auctions: state.auctions });
    }
    commit('removeAuctions');
  },

  async subscribeAuctions({ dispatch }, { auctions }) {
    await dispatch(
      'subscribeToChannels',
      setupChannelKeys(auctions),
      { root: true },
    );
  },

  async unsubscribeAuctions({ dispatch }, { auctions }) {
    await dispatch(
      'unsubscribeToChannels',
      setupChannelKeys(auctions),
      { root: true },
    );
  },

  async handleBidMessage(
    {
      commit,
      dispatch,
      state,
      rootGetters,
      rootState,
    },
    pubNubMessage,
  ) {
    const {
      auction: {
        current_bid: highBid,
        end_time: endTime,
        high_bidder_dealer_id: highBidderDealerId,
        high_bidder_id: highBidderUserId,
        id: auctionId,
        state: status,
      },
    } = pubNubMessage;

    const existingAuction = state.auctions.find((auction) => auction.id === auctionId);

    const isActiveSelling = rootState.route.meta.endpointContext === 'activeSelling';

    if (existingAuction) {
      const { currentUser } = rootGetters;

      const updatedValues = {
        buyerInfo: {
          dealerId: highBidderDealerId,
          userId: highBidderUserId,
          highBid,
        },
        participant: (isActiveSelling
          ? existingAuction.isParticipant
          : existingAuction.participant) || highBidderUserId === currentUser.id,
        canBid: highBidderUserId !== currentUser.id && status === 'active',
        statusPills: [],
        endTime,
        highBid,
        isMemberOfTopBiddingDealership: currentUser.isMemberOfWinningDealership(highBidderDealerId),
        isPrimaryTopBidder: highBidderUserId === currentUser.id,
      };

      const updatedAuction = isActiveSelling
        ? {
          ...cloneDeep(existingAuction.auction),
          ...updatedValues,
        }
        : {
          ...existingAuction,
          ...updatedValues,
        };

      updatedAuction.statusPills = adjustAuctionStatusPills(
        highBid,
        highBidderUserId,
        highBidderDealerId,
        currentUser,
        updatedAuction,
        isActiveSelling
          ? cloneDeep(existingAuction.auction)
          : existingAuction,
      );

      await commit('mutateAuctions', { auctions: [updatedAuction], isActiveSelling });
      await dispatch('updateAuctions', { auctionIds: [updatedAuction.id] });
    }
  },
};

export const mutations = {
  mutateAuctions(
    state,
    { auctions = [], isActiveSelling = false },
  ) {
    const localAuctions = [...state.auctions];

    auctions.forEach((auction) => {
      const existingAuctionIndex = localAuctions
        .findIndex((newAuction) => newAuction.id === auction.id);
      if (existingAuctionIndex > -1) {
        if (localAuctions[existingAuctionIndex].status === 'active'
          && localAuctions[existingAuctionIndex].status === auction.status
        ) {
          // If the new high bid received is lesser than the current high bid we have
          // do not update that active auction which is in the same auction status
          if (auction.highBid < localAuctions[existingAuctionIndex].highBid
          ) {
            return;
          }
        }

        // update auction, active selling needs the auction model
        localAuctions[existingAuctionIndex] = isActiveSelling
          ? new Auction({
            ...cloneDeep(localAuctions[existingAuctionIndex].auction),
            ...auction,
          })
          : {
            ...localAuctions[existingAuctionIndex],
            ...auction,
          };
      } else {
        // add auction, active selling needs the auction model
        localAuctions.push(isActiveSelling
          ? new Auction(auction)
          : auction);
      }
    });

    state.auctions = localAuctions;
  },

  removeAuctions(state) {
    state.auctions = [];
  },
};

export const getters = {
  getAuctions: (state) => state.auctions,
  getMyAcvAuctionById: (state) => (auctionId) => state.auctions.find(
    (auction) => auction.id === auctionId,
  ),
};

export const state = {
  auctions: [],
};

export default {
  namespaced: true,
  actions,
  mutations,
  getters,
  state,
};
